import { t } from 'i18next';

import { DesignToolSeatModel } from '../../../typings/api/skymap/rest/v0/.common';
import { UserByRequestResponse } from '../../../typings/api/skymap/rest/v0/user';
//@ts-ignore
import roleSettings from '../../static/configuration/roleSettings.json';
import { SkyMapAxiosServiceFactory } from '../services/axios/skymap-axios-service-factory';
import { ThemeManager } from '../theming/theme-manager';
import { licensePermissionChecks } from '../utils/permission-check/license';
import { assertNever, isDefined } from '../utils/variables';
import { ProjectStore } from './project-store';

export interface NavigationItem {
  id: string;
  index: number;
  nav: string;
  title: string;
}

export type Role = 'user' | 'companyAdmin' | 'organizationAdmin';

export class UserStore {
  static get instance() {
    return UserStore._instance;
  }

  private constructor() {
    /*  */
  }

  get user() {
    if (!isDefined(this._user)) {
      throw new Error('User not loaded into user store.');
    }

    return this._user;
  }

  private static _instance: UserStore = new UserStore();
  private _user?: UserByRequestResponse['data'];

  clear() {
    this._user = undefined;
  }

  async fetchActiveUser() {
    await this.fetchRequestingUser();
    this.setActiveRole(this.getMainNavItems()[0]);
    return this._user;
  }

  async reload() {
    if (!this._user || !this._user.email) {
      return;
    }
    await this.fetchRequestingUser();
  }

  private async fetchRequestingUser() {
    this._user = (
      await SkyMapAxiosServiceFactory.instance.createUserServiceV0().fetchRequestingUser({})
    ).data;
  }

  async getActiveUser() {
    // This is fail safe if a user tries to reload the browser while in the viewer.
    // If the user listed in the local storage have not been loaded, fetch the user data.
    //
    // Managing the user should be centralized, but waiting on the React conversion at this point.
    return this._user ?? (await this.fetchActiveUser());
  }

  getName() {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return isDefined(this._user) ? `${this._user.firstName} ${this._user.lastName}` : '';
  }

  getActiveUserId() {
    return isDefined(this._user) ? this._user.id : undefined;
  }

  getEmail() {
    return this._user?.email;
  }

  isOrganizationAdmin() {
    return this._user?.isOrganizationAdmin ?? false;
  }

  isCompanyAdmin() {
    return (this._user?.companyusers ?? []).length > 0;
  }

  hasDesignToolAccessForCurrentProject(): boolean {
    if (this.isOrganizationAdmin()) {
      return true;
    }

    if (
      this.isCompanyAdmin() &&
      (ThemeManager.instance.theme.themeName === 'moba' ||
        ProjectStore.instance?.isProjectOverridingDesignToolAccess)
    ) {
      return true;
    }

    if (!ProjectStore.instance.project?.companyId) {
      return false;
    }

    if (this.hasValidDesignToolSeat()) {
      return true;
    }

    if (licensePermissionChecks.hasValidLicense('DESIGN')) {
      return true;
    }

    return false;
  }

  hasValidDesignToolSeat() {
    const seats = this._user?.designToolSeats ?? [];

    const isSeatValid = (model: DesignToolSeatModel) => {
      if (model.companyId !== ProjectStore.instance.project!.companyId) {
        return false;
      }

      if (model.validUntil === undefined) {
        return true;
      }

      return new Date(model.validUntil).getTime() > new Date().getTime();
    };

    return seats.some(isSeatValid);
  }

  isLoaded() {
    return this._user !== undefined;
  }

  getMainNavItems() {
    return UserStore.getNavigationItems(this._user);
  }

  static getNavigationItems(user: any): NavigationItem[] {
    if (!user) {
      return [];
    }

    /**
     * Here we identify the use, and set the corresponding menu setup
     */
    // Always check if user is organisation administrator first
    // Else we will have issues later on
    if (user.isOrganizationAdmin) {
      user.roles = ['organizationAdmin'];
    } else if (user.companyusers.length > 0) {
      user.roles = ['companyAdmin'];
      if (user.projects.length > 0) {
        user.roles.push('user');
      }
    } else {
      user.roles = ['user'];
    }

    const tmpNavItems: { [key: string]: any } = {};
    for (const role of user.roles) {
      for (const setting of roleSettings[role]) {
        tmpNavItems[setting.id] = setting;
        tmpNavItems[setting.id].title = this.resolveTitle(role as Role);
      }
    }

    // Tried returning map results instead of double for-loop,
    // but results in invalid browser for some reason.
    // This will be removed when converted to React anyway. 🤷
    return Object.values(tmpNavItems);
  }

  private static resolveTitle(role: Role): any {
    switch (role) {
      case 'user':
        return t('roles.user', { ns: 'common' });
      case 'companyAdmin':
        return t('roles.companyAdmin', { ns: 'common' });
      case 'organizationAdmin':
        return t('roles.organizationAdmin', { ns: 'common' });
      default:
        assertNever(role);
    }
  }

  getMainNavState() {
    const roleKey = this.findRoleKeyByActiveRoleId();
    return isDefined(roleKey) ? roleSettings[roleKey][0].nav : undefined;
  }

  setActiveRole(role: any) {
    if (isDefined(role)) {
      localStorage.setItem('selectedRole', JSON.stringify(role));
    } else {
      localStorage.removeItem('selectedRole');
    }
  }

  getActiveRole() {
    const role = localStorage.getItem('selectedRole');
    return isDefined(role) ? JSON.parse(role) : null;
  }

  getActiveRoleNav() {
    const activeRole = this.getActiveRole();
    if (!activeRole || !activeRole.nav) {
      return this.getMainNavState();
    }
    return activeRole.nav;
  }

  hasPermission() {
    return true;
  }

  findRoleKeyByActiveRoleId() {
    const activeRole = this.getActiveRole();
    if (!isDefined(activeRole)) {
      return undefined;
    }

    for (const [key, value] of Object.entries(roleSettings as object)) {
      if ((value as any[])[0].id === activeRole.id) {
        return key;
      }
    }

    return undefined;
  }

  isActiveRoleProjectUser() {
    return this.findRoleKeyByActiveRoleId() === 'user';
  }

  isActiveRoleCompanyAdmin() {
    return this.findRoleKeyByActiveRoleId() === 'companyAdmin';
  }

  isActiveRoleOrganizationAdmin() {
    return this.findRoleKeyByActiveRoleId() === 'organizationAdmin';
  }

  getProjects() {
    return this._user?.projects;
  }

  getCompanyUsers() {
    return this._user?.companyusers;
  }
}
